import React, { useState } from 'react';
import { useThemeMode } from '../../ThemeContext';
import { getProfileCompanyPicUrl } from '../../helpers/url_helper';
import { formatDate } from '../../helpers/UtilsFunctions';

import TaskOptionsMenu from './TaskOptionsMenu';
import CalendarIcon from '../../assets/Calendar.svg';
import TargetIcon from '../../assets/Target.svg';
import ChatIcon from '../../assets/Chat.svg';

// Helper function to handle profile picture URL (same as DashboardLayout)
const getProfilePicUrl = (profilePic) => {
  if (!profilePic) return '';
  
  // Check if URL already starts with http (any protocol)
  if (profilePic.toLowerCase().startsWith('http://') || profilePic.toLowerCase().startsWith('https://')) {
    return profilePic; // Return as is if it's already a full URL
  } else {
    return getProfileCompanyPicUrl(profilePic); // Append profile/company pic URL prefix if it's a relative path
  }
};

const TaskCard = ({ task, onOptionsClick, onCardClick, isDragging = false }) => {
  const { mode } = useThemeMode();
  const [optionsAnchorEl, setOptionsAnchorEl] = useState(null);

  const handleOptionsOpen = (event) => {
    event.stopPropagation(); // Prevent card click when clicking options
    setOptionsAnchorEl(event.currentTarget);
  };

  const handleOptionsClose = () => {
    setOptionsAnchorEl(null);
  };

  const handleEdit = (task) => {
    onOptionsClick && onOptionsClick(task, 'edit');
  };

  const handleDelete = (task) => {
    onOptionsClick && onOptionsClick(task, 'delete');
  };

  const handleCardClick = () => {
    onCardClick && onCardClick(task);
  };

  // Helper function to get priority color
  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'urgent':
      case 'high':
        return '#f44336';
      case 'medium':
        return '#2196f3';
      case 'low':
        return '#ff9800';
      default:
        return '#666';
    }
  };

  // Helper function to get status color
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return '#4caf50';
      case 'in review':
        return '#FFC107'; // Brighter yellow for better visibility
      case 'in progress':
        return '#26C3C0';
      case 'to do':
        return '#6C757D'; // Grey color instead of white for visibility
      default:
        return '#666';
    }
  };

  return (
    <div 
      style={{
        backgroundColor: mode === 'dark' ? '#1a1a1a' : '#ffffff',
        border: `1px solid ${mode === 'dark' ? '#333' : '#e0e0e0'}`,
        borderRadius: '8px',
        padding: '20px',
        cursor: 'pointer',
        marginBottom: '12px',
        width: '100%',
        minWidth: '280px',
        maxWidth: '100%',
        boxShadow: mode === 'dark' ? '0 2px 6px rgba(0, 0, 0, 0.3)' : '0 2px 6px rgba(0, 0, 0, 0.1)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        opacity: isDragging ? 0.8 : 1,
        transform: isDragging ? 'rotate(2deg)' : 'none'
      }}
      className="task-card"
      onClick={handleCardClick}
      onMouseEnter={(e) => {
        if (!isDragging) {
          e.target.style.transform = 'translateY(-4px)';
          e.target.style.boxShadow = mode === 'dark' ? '0 8px 16px rgba(0, 0, 0, 0.4)' : '0 8px 16px rgba(0, 0, 0, 0.2)';
          e.target.style.borderColor = '#26C3C0';
          e.target.style.zIndex = '10';
          
          // Ensure all child elements don't have their own shadows or transforms
          const children = e.target.querySelectorAll('*');
          children.forEach(child => {
            if (child.style) {
              child.style.boxShadow = 'none';
              child.style.transform = 'none';
            }
          });
        }
      }}
      onMouseLeave={(e) => {
        if (!isDragging) {
          e.target.style.transform = 'translateY(0)';
          e.target.style.boxShadow = mode === 'dark' ? '0 2px 6px rgba(0, 0, 0, 0.3)' : '0 2px 6px rgba(0, 0, 0, 0.1)';
          e.target.style.borderColor = mode === 'dark' ? '#333' : '#e0e0e0';
          e.target.style.zIndex = 'auto';
          
          // Restore any original child element styles
          const children = e.target.querySelectorAll('*');
          children.forEach(child => {
            if (child.style) {
              child.style.boxShadow = '';
              child.style.transform = '';
            }
          });
        }
      }}
    >
      {/* Priority Chip at Top Left */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '12px'
      }}>
        <span style={{
          padding: '4px 10px',
          backgroundColor: getPriorityColor(task.priority),
          color: '#FFFFFF',
          borderRadius: '4px',
          fontSize: '10px',
          fontWeight: '600',
          fontFamily: 'Urbanist, Arial, sans-serif',
          textTransform: 'capitalize',
          minWidth: 'fit-content',
          paddingLeft: '13px',
          paddingRight: '13px'
        }}>
          {task.priority}
        </span>

        {/* Options Menu */}
        <TaskOptionsMenu
          task={task}
          onEdit={handleEdit}
          onDelete={handleDelete}
          anchorEl={optionsAnchorEl}
          onClose={handleOptionsClose}
          onOpen={handleOptionsOpen}
        />
      </div>

      {/* Task Title - Second Row */}
      <h4 style={{
        fontSize: '14px',
        fontWeight: '600',
        color: mode === 'dark' ? '#FFFFFF' : '#222222',
        margin: '0 0 12px 0',
        fontFamily: 'Urbanist, Arial, sans-serif',
        lineHeight: '1.3'
      }}>
        {task.title}
      </h4>

      {/* Task Description - Third Row */}
      {task.description && (
        <div className="task-card-description" style={{
          fontSize: '12px',
          color: mode === 'dark' ? '#B0B0B0 !important' : '#666666 !important',
          margin: '0 0 12px 0',
          lineHeight: '1.4',
          fontFamily: 'Urbanist, Arial, sans-serif',
          display: '-webkit-box',
          WebkitLineClamp: 4,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          whiteSpace: 'pre-wrap',
          wordWrap: 'break-word',
          textAlign: 'justify'
        }}
        dangerouslySetInnerHTML={{ __html: task.description }}
        />
      )}

      {/* Date Range - Fourth Row */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        marginBottom: '12px'
      }}>
        <img
          src={CalendarIcon}
          alt="Calendar"
          style={{
            width: '14px',
            height: '14px',
            filter: mode === 'dark' ? 'brightness(0) invert(1)' : 'brightness(0) saturate(100%) invert(40%)',
            opacity: 1
          }}
        />
        <span style={{
          fontSize: '13px',
          color: mode === 'dark' ? '#FFFFFF' : '#666666',
          fontFamily: 'Urbanist, Arial, sans-serif'
        }}>
          {task.start_date && task.due_date ? 
            `${formatDate(task.start_date) || 'Not set'} to ${formatDate(task.due_date) || 'Not set'}` : 
            'Dates not set'
          }
        </span>
      </div>

      {/* Task Footer - Workers, Documents, Comments */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        {/* Workers Section */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          {/* Worker Avatar */}
          {task.assigned_to_profile_pic ? (
            <img
              src={getProfilePicUrl(task.assigned_to_profile_pic)}
              alt="Profile"
              style={{
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                objectFit: 'cover',
                border: `2px solid ${mode === 'dark' ? '#333' : '#ffffff'}`,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
              onError={(e) => {
                // Replace the image with a fallback avatar when it fails to load
                const fallbackDiv = document.createElement('div');
                fallbackDiv.style.cssText = `
                  width: 24px;
                  height: 24px;
                  border-radius: 50%;
                  background-color: #26C3C0;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  color: #FFFFFF;
                  font-size: 12px;
                  font-weight: 600;
                  border: 2px solid ${mode === 'dark' ? '#333' : '#ffffff'};
                  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                `;
                fallbackDiv.textContent = task.assigned_to_full_name ? task.assigned_to_full_name.charAt(0).toUpperCase() : 'U';
                e.target.parentNode.replaceChild(fallbackDiv, e.target);
              }}
            />
          ) : (
            <div 
              style={{
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                backgroundColor: '#26C3C0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#FFFFFF',
                fontSize: '12px',
                fontWeight: '600',
                border: `2px solid ${mode === 'dark' ? '#333' : '#ffffff'}`,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            >
              {task.assigned_to_full_name ? task.assigned_to_full_name.charAt(0).toUpperCase() : 'U'}
            </div>
          )}
          
          <span style={{
            fontSize: '11px',
            color: mode === 'dark' ? '#FFFFFF' : '#666666',
            fontFamily: 'Urbanist, Arial, sans-serif',
            fontWeight: '500'
          }}>
            {task.assigned_to_full_name || 'Unassigned'}
          </span>
        </div>

        {/* Comment Count Section */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px'
        }}>
          <img
            src={ChatIcon}
            alt="Comments"
            style={{
              width: '16px',
              height: '16px',
              filter: mode === 'dark' ? 'brightness(0) invert(1)' : 'brightness(0) saturate(100%) invert(40%)',
              opacity: 0.7
            }}
          />
          <span style={{
            fontSize: '11px',
            color: mode === 'dark' ? '#FFFFFF' : '#666666',
            fontFamily: 'Urbanist, Arial, sans-serif',
            fontWeight: '500'
          }}>
            {task.comment_count || 0}
          </span>
        </div>
      </div>
    </div>
  );
};

export default TaskCard;
